// File: frontend/src/theme.js
import { createTheme, alpha } from "@mui/material/styles";

// ─── Shared design tokens ─────────────────────────────────────────────────────
const RADIUS = { sm: 6, md: 10, lg: 14, xl: 20 };
const FONT   = '"Inter", "DM Sans", system-ui, sans-serif';

// ─── Spacing scale (px) ───────────────────────────────────────────────────────
// 4/8 rhythm. In MUI `sx` prefer the numeric shorthand (1 unit = 8px → p:3 = 24px).
// This export is the single source of truth for the few non-MUI spots.
const SPACE = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32, xxxl: 48 };

// ─── Canonical type scale ─────────────────────────────────────────────────────
// The single source of truth for the design-system migration. Weights collapse
// to 400/500/600/700. NOTE: the legacy h4–h6 in the MUI typography block below
// are intentionally oversized and will be reconciled onto this scale page-by-page
// during rollout (changing them now would visually shift every existing screen).
const TYPE = {
  h1:        { fontSize: "2rem",     fontWeight: 700, lineHeight: 1.2 },
  h2:        { fontSize: "1.5rem",   fontWeight: 700, lineHeight: 1.25 },
  h3:        { fontSize: "1.25rem",  fontWeight: 600, lineHeight: 1.3 },
  subtitle1: { fontSize: "1rem",     fontWeight: 600, lineHeight: 1.4 },
  subtitle2: { fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.4 },
  body:      { fontSize: "0.95rem",  fontWeight: 400, lineHeight: 1.55 },
  label:     { fontSize: "0.8rem",   fontWeight: 600, lineHeight: 1.4 },
  caption:   { fontSize: "0.75rem",  fontWeight: 400, lineHeight: 1.5 },
  overline:  { fontSize: "0.72rem",  fontWeight: 700, lineHeight: 1.4, letterSpacing: "0.06em", textTransform: "uppercase" },
};

// ─── Palette ──────────────────────────────────────────────────────────────────
// Light mode: refined slate/emerald — NOT the same greens on white.
// The light accent is emerald-700: emerald-600 (#059669) measured 3.77:1 on
// white and 3.5:1 on the page, so every small emerald label, status badge,
// selected tab and outlined/contained button failed WCAG AA. This is the one
// place that decides it; dark mode keeps its own green (DARK.accent).
const LIGHT = {
  // Backgrounds — clean, neutral slate system (not warm stone). Layered so
  // surfaces read as distinct planes without harsh white-on-white.
  bg: {
    page:      "#F5F7FA",   // neutral slate-tinted page bg
    paper:     "#FFFFFF",   // primary card surface — clean white
    elevated:  "#FFFFFF",   // top-level dialogs / popovers
    sunken:    "#F1F5F9",   // slate-100 — inset fields, table header rows
    hover:     "#F1F5F9",   // slate-100 — row / action hover
    active:    "#E2E8F0",   // slate-200 — pressed / selected
  },
  // Brand green — deeper so it works on light backgrounds
  accent: {
    main:      "#047857",   // emerald-700 — 5.5:1 on paper, 5.1:1 on page, 4.8:1 on soft
    hover:     "#065F46",   // emerald-800 — contained-button hover
    soft:      "#D1FAE5",   // emerald-100 — badge backgrounds
    text:      "#065F46",   // emerald-800 — text on soft bg
    border:    "#6EE7B7",   // emerald-300 — subtle borders
  },
  // Neutrals — slate scale for a controlled, professional light UI
  text: {
    primary:   "#0F172A",   // slate-900 — deep, high-contrast
    secondary: "#475569",   // slate-600 — readable secondary (stronger than 500)
    // Was #94A3B8 (slate-400): 2.54:1 on the white paper surface, failing
    // WCAG AA (4.5:1) by nearly half. In this codebase `text.disabled` is not
    // mostly a disabled-control colour — it is the MUTED INFORMATIONAL tone,
    // used ~449 times for transaction references, payout references, KPI
    // captions and timestamps, i.e. text carrying real content that a reader
    // has to be able to read. slate-500 measures 4.77:1 on white and passes.
    // Genuinely disabled CONTROLS are held faint separately, by the explicit
    // `action.disabled` entries below — do not re-lighten this to make a
    // disabled button look right; fix action.disabled instead.
    disabled:  "#616F84",   // 5.12:1 on #FFFFFF paper, 4.77:1 on the #F5F7FA page
  },
  border: {
    default:   "rgba(15,23,42,0.10)",  // subtle neutral divider
    strong:    "rgba(15,23,42,0.16)",  // emphasised divider
  },
  // ── Form fields that must be FOUND (the "labelled field" pattern) ───────
  // Measured, not eyeballed. WCAG 1.4.11 asks 3:1 for a control's boundary;
  // the general `border.default` divider is ~1.2:1, which is why a required
  // input could read as empty page. Label is strong but below text.primary so
  // the page title and the entered value still lead.
  field: {
    label:       "#334155",   // 10.35:1 on white
    surface:     "#FFFFFF",
    border:      "#7C8799",   // 3.63:1 on paper, 3.38:1 on the page
    borderHover: "#475569",
  },
  // Status
  success:    { main: "#047857", light: "#D1FAE5", text: "#065F46" },
  warning:    { main: "#D97706", light: "#FEF3C7", text: "#92400E" },
  error:      { main: "#DC2626", light: "#FEE2E2", text: "#991B1B" },
  info:       { main: "#2563EB", light: "#DBEAFE", text: "#1E40AF" },
  // Money. Amber/gold is reserved for money figures (the Router Dashboard's
  // revenue and wallet). Deepened for contrast on white, as the green is; the
  // dark-mode values are DARK.gold. Previously held only inside the dashboard's
  // own token file.
  gold:       { main: "#B45309", bright: "#92400E", text: "#8A4B08", dim: "rgba(180,83,9,0.10)", border: "rgba(180,83,9,0.30)" },
  // Table row status colors
  unused:     { color: "#047857", bg: alpha("#047857", 0.1), border: alpha("#047857", 0.25) },
  used:       { color: "#64748B", bg: alpha("#64748B", 0.1), border: alpha("#64748B", 0.2) },
  expired:    { color: "#DC2626", bg: alpha("#DC2626", 0.08), border: alpha("#DC2626", 0.2) },
};

const DARK = {
  bg: {
    page:      "#060A0F",
    paper:     "#0D1117",
    elevated:  "#141920",
    sunken:    "#070B10",
    hover:     "rgba(255,255,255,0.032)",
    active:    "rgba(255,255,255,0.06)",
  },
  accent: {
    main:      "#4ADE80",   // original green — fine on dark
    hover:     "#22C55E",
    soft:      "rgba(74,222,128,0.12)",
    text:      "#4ADE80",
    border:    "rgba(74,222,128,0.3)",
  },
  gold: {
  main:   "#F0B429",
  bright: "#F5C842",
  dim:    "rgba(240,180,41,0.10)",
  border: "rgba(240,180,41,0.22)",
  text:   "#F5C842",
  },
  text: {
    primary:   "#F1F5F9",
    secondary: "#94A3B8",
    // Was #475569: 2.49:1 on the #0D1117 paper surface — the same failure as
    // light mode, and for the same reason. This measures 5.2:1.
    disabled:  "#78889B",
  },
  border: {
    default:   "rgba(255,255,255,0.08)",
    strong:    "rgba(255,255,255,0.14)",
  },
  // See LIGHT.field. Dark: a surface one step above the paper, a boundary
  // that clears 3:1 against paper (3.25), page (3.41) and elevated (3.03).
  field: {
    label:       "#D5DDE7",   // 13.8:1 on paper; below text.primary (16:1)
    surface:     "#121923",
    border:      "#5A6678",
    borderHover: "#8A97AB",
  },
  success:    { main: "#4ADE80", light: "rgba(74,222,128,0.12)", text: "#4ADE80" },
  warning:    { main: "#FBBF24", light: "rgba(251,191,36,0.12)",  text: "#FBBF24" },
  error:      { main: "#F87171", light: "rgba(248,113,113,0.12)", text: "#F87171" },
  info:       { main: "#60A5FA", light: "rgba(96,165,250,0.12)",  text: "#60A5FA" },
  unused:     { color: "#4ADE80", bg: "rgba(74,222,128,0.1)",   border: "rgba(74,222,128,0.25)" },
  used:       { color: "#94A3B8", bg: "rgba(148,163,184,0.1)", border: "rgba(148,163,184,0.2)" },
  expired:    { color: "#F87171", bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.2)" },
};

// WhatsApp's own brand green, the same in both modes because it is an external
// brand affordance. White on it is ~2:1, so the glyph and label use a deep
// green instead (7.5:1).
const WHATSAPP = { main: "#25D366", hover: "#1EBE5A", contrastText: "#072E16" };

// The landing hero fills its section with a photograph of a dark room, so its
// ground and its text are the same in BOTH modes — these are properties of the
// artwork, not of the theme. On the light theme, text.primary over that picture
// would be near-black type on a near-black background, and the section's own
// ground has to be dark too or the image's fade-in reads as a white flash.
// `ink` is sampled from the photograph's shadows so the two never seam.
const HERO_SCENE = { ink: "#080C11", text: "#F2F6FA" };

// ─── Theme builder ────────────────────────────────────────────────────────────
export function buildTheme(mode) {
  const p = mode === "dark" ? DARK : LIGHT;
  const isDark = mode === "dark";

  return createTheme({
    palette: {
      mode,
      background: {
        default: p.bg.page,
        paper:   p.bg.paper,
      },
      primary: {
        main:          p.accent.main,
        contrastText:  isDark ? "#000" : "#fff",
      },
      text: {
        primary:   p.text.primary,
        secondary: p.text.secondary,
        disabled:  p.text.disabled,
      },
      // DISABLED CONTROLS, held separately from `text.disabled`.
      //
      // text.disabled was raised to an AA-passing tone because this codebase
      // uses it for muted INFORMATION. A disabled control is a different idea:
      // it must read as unavailable, and WCAG 1.4.3 explicitly exempts
      // inactive controls from the contrast minimum. Without these entries,
      // raising text.disabled would have quietly made disabled buttons and
      // fields look enabled — the exact regression this split prevents.
      action: {
        disabled:           isDark ? "rgba(255,255,255,0.30)" : "rgba(15,23,42,0.28)",
        disabledBackground: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
        disabledOpacity:    0.38,
      },
      divider: p.border.default,
      // The labelled-field tokens (components/ui/FormField `labelAbove`).
      field: { ...p.field, placeholder: p.text.disabled, helper: p.text.secondary },
      // `text` is each tone's AA-safe colour for small text on its own tint
      // (StatusBadge). In dark mode it equals `main`.
      whatsapp: WHATSAPP,
      success: { main: p.success.main, text: p.success.text },
      warning: { main: p.warning.main, text: p.warning.text },
      error:   { main: p.error.main,   text: p.error.text },
      info:    { main: p.info.main,    text: p.info.text },
    },

    typography: {
      fontFamily: FONT,
      allVariants: { letterSpacing: "normal" },
      fontSize: 16,
      body1: { fontSize: "1.05rem", lineHeight: 1.6 },
      body2: { fontSize: "0.98rem", lineHeight: 1.55 },
      caption: { fontSize: "0.86rem", lineHeight: 1.5 },
      button: { fontSize: "0.98rem" },
      // Complete top-of-scale (additive — no existing screen uses h1–h3 / subtitle
      // / overline today, so these introduce the ramp without changing any page).
      h1: TYPE.h1,
      h2: TYPE.h2,
      h3: TYPE.h3,
      subtitle1: TYPE.subtitle1,
      subtitle2: TYPE.subtitle2,
      overline: TYPE.overline,
      // Legacy oversized headings — kept as-is until per-page reconciliation.
      h4: { fontSize: "2.15rem", lineHeight: 1.15, fontWeight: 800 },
      h5: { fontSize: "1.7rem", lineHeight: 1.2, fontWeight: 800 },
      h6: { fontSize: "1.28rem", lineHeight: 1.25, fontWeight: 800 },
    },

    shape: { borderRadius: RADIUS.md },

    components: {
      // ── Paper / Card surfaces ──────────────────────────────────────────────
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: p.bg.paper,
            // Light mode: add a very subtle top highlight
            ...(isDark ? {} : {
              boxShadow: "0 1px 0 0 rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.03)",
            }),
          },
        },
      },

      // ── Buttons ────────────────────────────────────────────────────────────
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: RADIUS.md,
            letterSpacing: "0.01em",
            minHeight: 38,
            padding: "8px 16px",
            fontSize: 15,
          },
          containedPrimary: {
            // ONE disabled look for every primary action. Without this the
            // green glow below survived the disabled state, so a disabled
            // "Confirm & Send" still read as ready. Green means actionable.
            "&.Mui-disabled": {
              background: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
              color: isDark ? "rgba(255,255,255,0.38)" : "rgba(15,23,42,0.38)",
              boxShadow: "none",
            },
            background: p.accent.main,
            color: isDark ? "#000" : "#fff",
            boxShadow: `0 1px 0 rgba(255,255,255,${isDark ? 0.06 : 0.12}) inset, 0 2px 6px ${alpha(p.accent.main, isDark ? 0.25 : 0.3)}`,
            "&:hover": {
              background: p.accent.hover,
              boxShadow: `0 1px 0 rgba(255,255,255,${isDark ? 0.06 : 0.12}) inset, 0 4px 12px ${alpha(p.accent.main, isDark ? 0.35 : 0.4)}`,
            },
          },
          outlinedPrimary: {
            borderColor: p.accent.border,
            color: p.accent.main,
            "&:hover": {
              backgroundColor: p.accent.soft,
              borderColor: p.accent.main,
            },
          },
        },
      },

      // ── Icon buttons ───────────────────────────────────────────────────────
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.md,
            transition: "all 0.18s ease",
            "&:hover": {
              backgroundColor: isDark ? "rgba(255,255,255,0.06)" : p.bg.hover,
            },
          },
        },
      },

      // ── Text fields ────────────────────────────────────────────────────────
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.md,
            backgroundColor: isDark ? "rgba(255,255,255,0.02)" : p.bg.elevated,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: p.border.default,
              transition: "border-color 0.18s",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? p.border.strong : p.border.strong,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: p.accent.main,
              borderWidth: 1.5,
            },
          },
          input: {
            fontSize: 15,
            paddingTop: 10,
            paddingBottom: 10,
            "&::placeholder": { color: p.text.disabled, opacity: 1 },
          },
        },
      },

      // ── Select ─────────────────────────────────────────────────────────────
      MuiSelect: {
        styleOverrides: {
          root: { borderRadius: RADIUS.md },
        },
      },

      // ── Menu / MenuItem ────────────────────────────────────────────────────
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: RADIUS.lg,
            border: `1px solid ${p.border.default}`,
            backgroundColor: p.bg.elevated,
            boxShadow: isDark
              ? "0 8px 32px rgba(0,0,0,0.6)"
              : "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: 15,
            borderRadius: RADIUS.sm,
            margin: "2px 6px",
            padding: "8px 12px",
            "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.05)" : p.bg.hover },
            "&.Mui-selected": {
              backgroundColor: p.accent.soft,
              color: p.accent.main,
              "&:hover": { backgroundColor: p.accent.soft },
            },
          },
        },
      },

      // ── Chips ──────────────────────────────────────────────────────────────
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 99,
            fontWeight: 600,
            fontSize: 13.5,
            letterSpacing: "0.02em",
            minHeight: 24,
          },
          colorSuccess: {
            backgroundColor: p.success.light,
            color: p.success.text,
          },
          colorError: {
            backgroundColor: p.error.light,
            color: p.error.text,
          },
          colorWarning: {
            backgroundColor: p.warning.light,
            color: p.warning.text,
          },
          colorInfo: {
            backgroundColor: p.info.light,
            color: p.info.text,
          },
        },
      },

      // ── Dialog ─────────────────────────────────────────────────────────────
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: RADIUS.xl,
            backgroundImage: "none",
            backgroundColor: p.bg.elevated,
            border: `1px solid ${p.border.default}`,
            boxShadow: isDark
              ? "0 24px 64px rgba(0,0,0,0.8)"
              : "0 24px 64px rgba(0,0,0,0.18), 0 8px 24px rgba(0,0,0,0.08)",
          },
        },
      },

      // ── Table ──────────────────────────────────────────────────────────────
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-head": {
              backgroundColor: isDark ? p.bg.sunken : p.bg.sunken,
              fontWeight: 700,
              fontSize: 13,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: p.text.secondary,
              borderBottom: `1px solid ${p.border.default}`,
              padding: "13px 16px",
              whiteSpace: "nowrap",
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:hover td": {
              backgroundColor: isDark ? p.bg.hover : p.bg.hover,
            },
            "& td": {
              transition: "background-color 0.12s",
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: p.border.default,
            padding: "13px 16px",
            fontSize: 15,
          },
        },
      },

      // ── Divider ────────────────────────────────────────────────────────────
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: p.border.default },
        },
      },

      // ── Alert ──────────────────────────────────────────────────────────────
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.md,
            fontSize: 15,
            border: "1px solid",
          },
          standardSuccess: {
            backgroundColor: p.success.light,
            color: p.success.text,
            borderColor: alpha(p.success.main, 0.3),
          },
          standardError: {
            backgroundColor: p.error.light,
            color: p.error.text,
            borderColor: alpha(p.error.main, 0.3),
          },
          standardWarning: {
            backgroundColor: p.warning.light,
            color: p.warning.text,
            borderColor: alpha(p.warning.main, 0.3),
          },
          standardInfo: {
            backgroundColor: p.info.light,
            color: p.info.text,
            borderColor: alpha(p.info.main, 0.3),
          },
        },
      },

      // ── Tabs ───────────────────────────────────────────────────────────────
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            fontSize: 15,
            minHeight: 44,
            color: p.text.secondary,
            "&.Mui-selected": { color: p.accent.main },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { backgroundColor: p.accent.main, height: 2, borderRadius: 99 },
        },
      },

      // ── Linear Progress ────────────────────────────────────────────────────
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 99,
            backgroundColor: isDark ? "rgba(255,255,255,0.06)" : p.border.default,
          },
        },
      },

      // ── Tooltip ────────────────────────────────────────────────────────────
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? "#1E2530" : "#0F172A",
            color: isDark ? "#E2E8F0" : "#F8FAFC",
            fontSize: 14,
            fontWeight: 500,
            borderRadius: RADIUS.sm,
            padding: "5px 10px",
            boxShadow: isDark
              ? "0 4px 16px rgba(0,0,0,0.5)"
              : "0 4px 16px rgba(0,0,0,0.2)",
          },
          arrow: {
            color: isDark ? "#1E2530" : "#0F172A",
          },
        },
      },

      // ── Popover ────────────────────────────────────────────────────────────
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: RADIUS.lg,
            border: `1px solid ${p.border.default}`,
            backgroundColor: p.bg.elevated,
            boxShadow: isDark
              ? "0 12px 40px rgba(0,0,0,0.6)"
              : "0 12px 40px rgba(0,0,0,0.14), 0 4px 12px rgba(0,0,0,0.06)",
          },
        },
      },

      // ── Skeleton ───────────────────────────────────────────────────────────
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "#E2E8F0",
            "&::after": {
              background: isDark
                ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)"
                : "linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)",
            },
          },
        },
      },

      // ── Card (mirror the Paper surface + a restrained border) ───────────────
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: p.bg.paper,
            border: `1px solid ${p.border.default}`,
            borderRadius: RADIUS.lg,
            ...(isDark ? {} : {
              boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
            }),
          },
        },
      },

      // ── Input label — readable contrast in both themes ──────────────────────
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: p.text.secondary,
            "&.Mui-focused": { color: p.accent.main },
            "&.Mui-error": { color: p.error.main },
          },
        },
      },

      // ── Table container / pagination ────────────────────────────────────────
      MuiTablePagination: {
        styleOverrides: {
          root: { color: p.text.secondary, borderTop: `1px solid ${p.border.default}` },
          selectIcon: { color: p.text.secondary },
        },
      },

      // ── Toggle buttons (segmented controls) ─────────────────────────────────
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderColor: p.border.default,
            color: p.text.secondary,
            "&.Mui-selected": {
              backgroundColor: p.accent.soft,
              color: p.accent.main,
              "&:hover": { backgroundColor: p.accent.soft },
            },
          },
        },
      },

      // ── List item button (menus, drawers) — consistent selected/hover ───────
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.sm,
            "&.Mui-selected": {
              backgroundColor: p.accent.soft,
              "&:hover": { backgroundColor: p.accent.soft },
            },
          },
        },
      },

      // ── Dialog sub-parts — consistent rhythm across the app ─────────────────
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontWeight: 800, fontSize: "1.15rem", padding: "20px 24px 8px" },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { padding: "8px 24px" },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: { padding: "12px 24px 20px", gap: 8 },
        },
      },

      // ── Selection controls — brand accent ───────────────────────────────────
      MuiCheckbox: {
        styleOverrides: {
          root: { color: p.text.disabled, "&.Mui-checked": { color: p.accent.main } },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: { color: p.text.disabled, "&.Mui-checked": { color: p.accent.main } },
        },
      },

      // ── Global baseline — body bg, custom scrollbars, keyboard focus ring ───
      MuiCssBaseline: {
        styleOverrides: {
          "*, *::before, *::after": { boxSizing: "border-box" },
          body: {
            backgroundColor: p.bg.page,
            color: p.text.primary,
          },
          // Consistent, theme-aware scrollbars (no jarring default light bars).
          "*::-webkit-scrollbar": { width: 8, height: 8 },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: isDark ? "rgba(255,255,255,0.14)" : "rgba(15,23,42,0.18)",
            borderRadius: 99,
          },
          "*::-webkit-scrollbar-track": { background: "transparent" },
          // Single, consistent keyboard focus ring across the app.
          "a:focus-visible, button:focus-visible, [role='button']:focus-visible, .MuiButtonBase-root:focus-visible": {
            outline: `2px solid ${p.accent.main}`,
            outlineOffset: 2,
          },
        },
      },
    },
  });
}

// Export design tokens for use in components (single source of truth).
export { LIGHT, DARK, RADIUS, SPACE, TYPE, WHATSAPP, HERO_SCENE };
// Add this alias to match what your components are expecting
export { buildTheme as getAppTheme };
